/**
 * Soffits (owner, 2026-07-29 → built 2026-08-05).
 *
 * A soffit is "a box at the top of a wall that drops a stated amount below the
 * ceiling and projects a stated depth into the room, blocking the wall(s) it
 * runs into — basically a very large obstruction" (owner). Modelling it as an
 * obstruction gives its position, width, height, depth and both drawings for
 * free; what it adds is a HEIGHT CAP on the cabinetry beneath it.
 *
 * The owner's rule: "the height of the bottom of the soffit would be treated as
 * the ceiling height for sections that are located below the soffit". So a
 * soffit does not introduce a new concept into the height chain — it replaces
 * the datum at the top of the one that already exists (ADR 0050):
 *
 *   soffit bottom  →  this wall's target  →  the space target  →  the ceiling
 *
 * This module is the pure part: which sections a soffit sits over, and how low
 * it caps them. Everything else is placement and drawing, which obstructions
 * already do.
 */

/**
 * The lowest soffit bottom over a section's span, or null when the section is
 * in the clear.
 *
 * Spans are along-the-wall distances, so this is one-dimensional overlap. A
 * section merely TOUCHING a soffit's edge is not under it — cabinetry ends
 * where the soffit begins, and capping it there would shorten a run for no
 * reason. Hence strict overlap, with a tolerance so float noise cannot
 * manufacture a cap.
 *
 * The LOWEST bottom wins when several soffits overlap one section: the
 * cabinetry has to clear all of them, so the tightest constraint is the real
 * one.
 *
 * @param {{startUnits:number, endUnits:number}} span the section, along the wall
 * @param {Array<{startUnits:number, endUnits:number, bottomUnits:number}>} soffits
 * @param {number} [tol] slack, in units, for float arithmetic
 * @returns {number|null} the capping height above the floor, or null
 */
export function soffitCapOver(span, soffits, tol = 0.01) {
  if (!span || !Array.isArray(soffits) || soffits.length === 0) return null;
  const start = Number(span.startUnits);
  const end = Number(span.endUnits);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end - start <= tol) return null;

  let lowest = null;
  for (const s of soffits) {
    const sStart = Number(s?.startUnits);
    const sEnd = Number(s?.endUnits);
    const bottom = Number(s?.bottomUnits);
    if (!Number.isFinite(sStart) || !Number.isFinite(sEnd) || !Number.isFinite(bottom)) continue;
    if (bottom <= 0) continue; // a soffit reaching the floor caps nothing usefully
    // Strict overlap: shared edges do not count.
    if (sEnd - start <= tol || end - sStart <= tol) continue;
    if (lowest === null || bottom < lowest) lowest = bottom;
  }
  return lowest;
}

/**
 * A soffit's bottom, from the drop the designer states.
 *
 * The designer measures a soffit as "it comes down 12 inches from the
 * ceiling", not as "its underside is at 92½ inches" — the drop is the same on
 * every job, the resulting height depends on the room. Storing the drop and
 * deriving the bottom means a soffit stays right when the ceiling height is
 * corrected.
 *
 * @param {number} ceilingUnits the ceiling at that point (a sloped wall's
 *   ceiling varies along it — pass the height where the soffit is)
 * @param {number} dropUnits how far it hangs below the ceiling
 * @returns {number} height above the floor, never negative
 */
export function soffitBottomUnits(ceilingUnits, dropUnits) {
  const ceiling = Number(ceilingUnits);
  const drop = Number(dropUnits);
  if (!Number.isFinite(ceiling) || !Number.isFinite(drop)) return 0;
  const bottom = ceiling - Math.max(0, drop);
  return bottom > 0 ? bottom : 0;
}
